import { entityColorVar, semanticVar } from '@hoopoe/shared';
import { Lock } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Tooltip } from '../ui/tooltip';

export const ENTITY_KINDS = ['actor_person', 'actor_org', 'event', 'location', 'asset', 'document', 'case'] as const;
export type EntityKindShape = (typeof ENTITY_KINDS)[number];
export type ReviewStatus = 'verified' | 'unreviewed' | 'disputed' | 'rejected';

export const KIND_LABEL: Record<EntityKindShape, string> = {
  actor_person: 'Person',
  actor_org: 'Organization',
  event: 'Event',
  location: 'Place',
  asset: 'Asset',
  document: 'Source',
  case: 'Case',
};

/** Shape per kind (16×16 box), so color is never the only signal (PLAN §12.2). Graphs reuse it. */
export function Shape({ kind }: { kind: EntityKindShape }) {
  switch (kind) {
    case 'actor_person':
      return <circle cx="8" cy="8" r="6" />;
    case 'actor_org':
      return <rect x="2.5" y="2.5" width="11" height="11" rx="1.5" />;
    case 'event':
      return <path d="M8 1.5 14.5 8 8 14.5 1.5 8Z" />;
    case 'location':
      return <path d="M8 15s5-5 5-8.5a5 5 0 0 0-10 0C3 10 8 15 8 15Z" />;
    case 'asset':
      return <path d="M8 1.5 13.6 4.75v6.5L8 14.5 2.4 11.25v-6.5Z" />;
    case 'document':
      return <path d="M3.5 1.5h6l3 3v10h-9Z" />;
    case 'case':
      return <path d="M2 4.5h12v9H2ZM5.5 4.5V2.5h5v2" />;
  }
}

export const STATUS_STROKE: Record<ReviewStatus, { dash?: string; width: number; label: string }> = {
  verified: { width: 1.25, label: 'Verified' },
  unreviewed: { dash: '2 1.5', width: 1.25, label: 'Unreviewed: extracted by AI, not yet checked by a person' },
  disputed: { width: 2.5, label: 'Disputed: sources disagree' },
  rejected: { dash: '1 2', width: 1, label: 'Rejected' },
};

/**
 * The entity's visual mark: fill = "Color by" slot, shape = kind, outline = review status, 🔒 = confidential,
 * opacity = rule "fade", ring = rule "highlight ring". `why` explains the color on hover ("Rule #3: Tag = Donor").
 */
export function EntityMark({
  kind,
  colorSlot,
  status = 'verified',
  confidential = false,
  why,
  fade = false,
  ring = false,
  ringSlot = null,
  size = 16,
  className,
  plain = false,
}: {
  kind: EntityKindShape;
  colorSlot?: number | null;
  status?: ReviewStatus;
  confidential?: boolean;
  why?: string;
  fade?: boolean;
  ring?: boolean;
  ringSlot?: number | null;
  size?: number;
  className?: string;
  /**
   * Inside a control that is itself the interactive element (a ⌘K option, a menu item): no tooltip, not focusable,
   * hidden from assistive tech (the control's own text carries kind, status and sensitivity).
   */
  plain?: boolean;
}) {
  const s = STATUS_STROKE[status];
  const label = [KIND_LABEL[kind], s.label, confidential ? 'Confidential' : null, why].filter(Boolean).join(' · ');
  const common = {
    className: cn('relative inline-flex shrink-0 items-center rounded-full', (status === 'rejected' || fade) && 'opacity-40', className),
    style: ring ? { boxShadow: `0 0 0 2px ${ringSlot ? entityColorVar(ringSlot) : semanticVar('foreground')}` } : undefined,
    'data-entity-kind': kind,
    'data-review-status': status,
    'data-color-slot': colorSlot ?? 'none',
    'data-faded': fade || undefined,
    'data-ring': ring || undefined,
  };
  const inner = (
    <>
      <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
        <g
          fill={entityColorVar(colorSlot)}
          stroke={semanticVar('foreground')}
          strokeOpacity={status === 'disputed' ? 0.9 : 0.45}
          strokeWidth={s.width}
          strokeDasharray={s.dash}
        >
          <Shape kind={kind} />
        </g>
      </svg>
      {status === 'disputed' && <span className="ms-0.5 text-[10px] leading-none text-warning-foreground">⚠</span>}
      {confidential && <Lock className="ms-0.5 size-3 text-muted-foreground" aria-hidden="true" />}
    </>
  );
  if (plain)
    return (
      <span {...common} aria-hidden="true">
        {inner}
      </span>
    );
  return (
    <Tooltip content={label}>
      <span
        {...common}
        role="img"
        aria-label={label}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: the mark's tooltip ("why this color?") must be keyboard reachable
        tabIndex={0}
      >
        {inner}
      </span>
    </Tooltip>
  );
}
